import type { Household, UserDataObj } from "src/types/snugtotal";

/**
 * Lightweight, display-only derivation of the household document checklist rows
 * (one row per person per document type), mirroring the status logic used by
 * `household-checklist-express`. Kept dependency-free so it can be used in the
 * always-mounted progress context without pulling in the heavy checklist module.
 */

export type DocRowStatus =
  | "signed"
  | "ready"
  | "generating"
  | "in_progress"
  | "not_started";

export interface DocRow {
  id: string;
  title: string;
  personName: string;
  status: DocRowStatus;
}

const notEmpty = <T,>(v: T | null | undefined): v is T => v != null;

const hasApproved = (docs?: any[]): boolean =>
  docs?.some(
    (d) =>
      d?.status === "APPROVED" ||
      d?.status === "ANALYSIS_PENDING" ||
      d?.status === "ANALYSIS_COMPLETE"
  ) ?? false;
const hasPending = (docs?: any[]): boolean =>
  docs?.some((d) => d?.status === "PENDING" || d?.status === "REVIEWABLE") ??
  false;
const hasInProgress = (docs?: any[]): boolean =>
  docs?.some((d) => d?.status === "IN_PROGRESS") ?? false;

const getBestDocument = (docs?: any[]): any | undefined => {
  if (!docs || docs.length === 0) return undefined;
  return (
    docs.find(
      (d) =>
        d?.status === "APPROVED" ||
        d?.status === "ANALYSIS_PENDING" ||
        d?.status === "ANALYSIS_COMPLETE"
    ) ||
    docs.find((d) => d?.status === "PENDING" || d?.status === "REVIEWABLE") ||
    docs.find((d) => d?.status === "IN_PROGRESS") ||
    docs[0]
  );
};

const toRowStatus = (docs?: any[]): DocRowStatus => {
  if (hasApproved(docs)) {
    const best = getBestDocument(docs);
    const sub = best?.documents ?? [];
    const allSigned =
      sub.length > 0 && sub.every((d: any) => d?.is_signed === true);
    return allSigned ? "signed" : "ready";
  }
  if (hasPending(docs)) return "generating";
  if (hasInProgress(docs)) return "in_progress";
  return "not_started";
};

const firstName = (full?: string | null): string =>
  full ? full.trim().split(/\s+/)[0] : "Member";

export const buildDocumentRows = (hh?: Household | null): DocRow[] => {
  if (!hh?.head_of_household) return [];

  const hoh = hh.head_of_household;
  const members = hh.household_member_roles ?? [];
  const spouse =
    members.find((m) => m.role === "SPOUSE")?.role_target_user_data ?? null;

  const epf = (hoh.estate_plan_foundation ?? "") as string;

  const allowWillsTrusts =
    hh.household_role?.checklist_display_willstrusts ?? true;
  const allowFpoa = hh.household_role?.checklist_display_fpoa ?? true;
  const allowHcd = hh.household_role?.checklist_display_hcd ?? true;

  const rows: DocRow[] = [];

  // ---- Wills & Trusts ------------------------------------------------------
  let wtJoint = false;
  let wtLabel = "Will or Trust";
  let wtPeople: UserDataObj[] = [hoh];

  if (allowWillsTrusts) {
    if (epf === "joint_trust") {
      wtJoint = true;
      wtLabel = "Living Trust";
    } else if (epf === "single_trust") {
      wtLabel = "Living Trust";
      wtPeople = [hoh];
    } else if (epf === "two_trusts") {
      wtLabel = "Living Trust";
      wtPeople = [hoh, spouse].filter(notEmpty);
    } else if (epf === "single_will") {
      wtLabel = "Last Will & Testament";
      wtPeople = [hoh];
    } else if (epf === "two_wills") {
      wtLabel = "Last Will & Testament";
      wtPeople = [hoh, spouse].filter(notEmpty);
    } else if (epf === "attorney") {
      wtLabel = "Will or Trust";
      wtPeople = [hoh];
    } else {
      // skip / unset
      wtLabel = "Will or Trust";
      wtPeople = [hoh];
    }

    if (wtJoint) {
      const names = [hoh, spouse].filter(notEmpty).map((p) => firstName(p.full_name));
      rows.push({
        id: "jointTrust",
        title: wtLabel,
        personName: names.join(" & "),
        status: toRowStatus(hoh.wills_and_trusts as any),
      });
    }
  }

  const wtIds = new Set(wtPeople.map((p) => p.ud_id));
  const isUnsetEpf = epf === "skip" || epf === "";

  // People who get individual rows: head + non-read-only members.
  const everyone: UserDataObj[] = [
    hoh,
    ...members
      .filter((r) => r.is_read_only !== true)
      .map((r) => r.role_target_user_data),
  ].filter(notEmpty);

  everyone.forEach((p) => {
    if (allowWillsTrusts && !wtJoint && wtIds.has(p.ud_id)) {
      rows.push({
        id: `${p.ud_id}-lwt`,
        title: wtLabel,
        personName: p.full_name || "Member",
        status: isUnsetEpf ? "not_started" : toRowStatus(p.wills_and_trusts as any),
      });
    }
    if (allowFpoa && (p.include_fpoa ?? true)) {
      rows.push({
        id: `${p.ud_id}-fpoa`,
        title: "Financial Power of Attorney",
        personName: p.full_name || "Member",
        status: toRowStatus(p.financial_powers_of_attorney as any),
      });
    }
    if (allowHcd && (p.include_hcd ?? true)) {
      rows.push({
        id: `${p.ud_id}-hcd`,
        title: "Health Care Directive",
        personName: p.full_name || "Member",
        status: toRowStatus(p.healthcare_directives as any),
      });
    }
  });

  return rows;
};

/** A document row is "created" once it's been generated (ready to sign or signed). */
export const isDocRowComplete = (status: DocRowStatus): boolean =>
  status === "ready" || status === "signed";
